import { useEffect, useState } from 'react';

/**
 * useCanHover
 *
 * A hook that detects whether the user's current device supports hover interactions
 * with a fine pointer (e.g., a mouse or trackpad).
 *
 * This is useful when you want to conditionally render UI elements or features (such as hover images)
 * only on devices that can actually benefit from them (like desktops),
 * and avoid unnecessary behavior or network usage on touch devices like tablets and phones.
 *
 * Internally uses the `window.matchMedia('(hover: hover) and (pointer: fine)')` API.
 *
 * @returns {boolean} `true` if the device supports hover with a fine pointer, `false` otherwise.
 *
 * @example
 * const canHover = useCanHover();
 * if (canHover) {
 *   // Render hover-dependent UI
 * }
 */

export const useCanHover = () => {
    const [canHover, setCanHover] = useState(false);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const mediaQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
        const updateHover = (e: MediaQueryList | MediaQueryListEvent) => {
            setCanHover(e.matches);
        };

        updateHover(mediaQuery);

        mediaQuery.addEventListener?.('change', updateHover);

        return () => {
            mediaQuery.removeEventListener?.('change', updateHover);
        };
    }, []);

    return canHover;
};
